/** Wizard step **3** generation policy fields (**DD-SRS-F-TRAINING-QUIZ.15**, **.20**). */

export const DEFAULT_QUESTIONS_PER_ATTEMPT = 10;
export const DEFAULT_OPTION_COUNT = 4;
export const MIN_QUESTIONS_PER_ATTEMPT = 1;
export const MAX_QUESTIONS_PER_ATTEMPT = 50;
export const MIN_OPTION_COUNT = 2;
export const MAX_OPTION_COUNT = 12;
export const MAX_POOL_GENERATION_COUNT = 100;
export const DEFAULT_MIN_CORRECT_OPTIONS = 1;
export const DEFAULT_MAX_CORRECT_OPTIONS = 1;

export interface GenerationSettingsValues {
  questionsPerAttempt: number;
  poolGenerationCount: number;
  optionCount: number;
  minCorrectOptions: number;
  maxCorrectOptions: number;
}

function readBoundedInt(
  raw: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  let n: number | null = null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    n = Math.floor(raw);
  } else if (typeof raw === "string" && /^\d+$/.test(raw.trim())) {
    n = parseInt(raw.trim(), 10);
  }
  if (n === null) {
    return fallback;
  }
  return Math.max(min, Math.min(max, n));
}

/** Default draft pool size when **`pool_generation_count`** is omitted (**2x** quiz length). */
export function defaultPoolGenerationCount(questionsPerAttempt: number): number {
  const qpa = Math.max(
    MIN_QUESTIONS_PER_ATTEMPT,
    Math.min(MAX_QUESTIONS_PER_ATTEMPT, questionsPerAttempt),
  );
  return Math.min(MAX_POOL_GENERATION_COUNT, Math.max(1, qpa * 2));
}

export function readQuestionsPerAttempt(
  configuration: Record<string, unknown> | undefined,
): number {
  return readBoundedInt(
    configuration?.questions_per_attempt,
    MIN_QUESTIONS_PER_ATTEMPT,
    MAX_QUESTIONS_PER_ATTEMPT,
    DEFAULT_QUESTIONS_PER_ATTEMPT,
  );
}

export function readOptionCount(
  configuration: Record<string, unknown> | undefined,
): number {
  return readBoundedInt(
    configuration?.option_count,
    MIN_OPTION_COUNT,
    MAX_OPTION_COUNT,
    DEFAULT_OPTION_COUNT,
  );
}

export function readPoolGenerationCount(
  configuration: Record<string, unknown> | undefined,
  questionsPerAttempt: number,
): number {
  const raw = configuration?.pool_generation_count;
  if (raw === undefined || raw === null || raw === "") {
    return defaultPoolGenerationCount(questionsPerAttempt);
  }
  return readBoundedInt(
    raw,
    1,
    MAX_POOL_GENERATION_COUNT,
    defaultPoolGenerationCount(questionsPerAttempt),
  );
}

/**
 * Clamps min and max correct counts into [1, optionCount] and ensures max >= min.
 *
 * @param optionCount - Bounded MC option count (MIN_OPTION_COUNT through MAX_OPTION_COUNT).
 * @param minCorrect - Desired minimum correct answers before clamping.
 * @param maxCorrect - Desired maximum correct answers before clamping.
 */
export function clampGenerationShapeBounds(
  optionCount: number,
  minCorrect: number,
  maxCorrect: number,
): Pick<GenerationSettingsValues, "minCorrectOptions" | "maxCorrectOptions"> {
  const oc = Math.max(
    MIN_OPTION_COUNT,
    Math.min(MAX_OPTION_COUNT, Math.floor(optionCount)),
  );
  let minC = Math.max(1, Math.min(oc, Math.floor(minCorrect)));
  let maxC = Math.max(1, Math.min(oc, Math.floor(maxCorrect)));
  if (maxC < minC) {
    maxC = minC;
  }
  return { minCorrectOptions: minC, maxCorrectOptions: maxC };
}

export function settingsFromConfiguration(
  configuration: Record<string, unknown> | undefined,
): GenerationSettingsValues {
  const questionsPerAttempt = readQuestionsPerAttempt(configuration);
  const optionCount = readOptionCount(configuration);
  const rawMin = readBoundedInt(
    configuration?.min_correct_options,
    1,
    optionCount,
    DEFAULT_MIN_CORRECT_OPTIONS,
  );
  const rawMax = readBoundedInt(
    configuration?.max_correct_options,
    1,
    optionCount,
    DEFAULT_MAX_CORRECT_OPTIONS,
  );
  const { minCorrectOptions, maxCorrectOptions } = clampGenerationShapeBounds(
    optionCount,
    rawMin,
    rawMax,
  );
  return {
    questionsPerAttempt,
    poolGenerationCount: readPoolGenerationCount(
      configuration,
      questionsPerAttempt,
    ),
    optionCount,
    minCorrectOptions,
    maxCorrectOptions,
  };
}

export function configurationFromSettings(
  values: GenerationSettingsValues,
): Record<string, number> {
  return {
    questions_per_attempt: values.questionsPerAttempt,
    pool_generation_count: values.poolGenerationCount,
    option_count: values.optionCount,
    min_correct_options: values.minCorrectOptions,
    max_correct_options: values.maxCorrectOptions,
  };
}

/** Returns a catalog key when invalid, else **null**. */
export function validateGenerationSettings(
  values: GenerationSettingsValues,
): "quiz.wizard.generation_shape_invalid" | null {
  const { questionsPerAttempt, poolGenerationCount, optionCount } = values;
  const minCorrect = values.minCorrectOptions;
  const maxCorrect = values.maxCorrectOptions;
  if (
    questionsPerAttempt < MIN_QUESTIONS_PER_ATTEMPT ||
    questionsPerAttempt > MAX_QUESTIONS_PER_ATTEMPT
  ) {
    return "quiz.wizard.generation_shape_invalid";
  }
  if (
    poolGenerationCount < 1 ||
    poolGenerationCount > MAX_POOL_GENERATION_COUNT
  ) {
    return "quiz.wizard.generation_shape_invalid";
  }
  if (optionCount < MIN_OPTION_COUNT || optionCount > MAX_OPTION_COUNT) {
    return "quiz.wizard.generation_shape_invalid";
  }
  if (
    minCorrect < 1 ||
    maxCorrect < minCorrect ||
    maxCorrect > optionCount
  ) {
    return "quiz.wizard.generation_shape_invalid";
  }
  return null;
}
